import { Link } from 'react-router-dom';
import BlueDotsBackground from '../BlueDotsBackground';

export default function Dispute() {
  const handleScrollToExecutiveSummary = () => {
    const section = document.getElementById('executive-summary');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const whatYouReceive = {
    compliance: [
      {
        text: 'Complete inventory of open Medicaid disputes',
        icon: 'fas fa-database',
      },
      {
        text: 'Probability of success scoring for every dispute',
        icon: 'fas fa-chart-pie',
      },
      {
        text: 'Risk-adjusted financial exposure analysis',
        icon: 'fas fa-balance-scale',
      },
      {
        text: 'Executive dashboard and management reporting',
        icon: 'fas fa-display',
      },
    ],
    roi: [
      {
        text: 'Validation of available Claim Level Detail (CLD)',
        icon: 'fas fa-check-double',
      },
      {
        text: 'Analysis using dozens of proprietary claim validations',
        icon: 'fas fa-magnifying-glass',
      },
      { text: 'Estimated recoverable dollars', icon: 'fas fa-dollar-sign' },
      { text: 'State-ready documentation packages', icon: 'fas fa-file-pdf' },
    ],
  };

  const executiveOutcomes = [
    'Understand your true recoverable dispute value',
    'Prioritize disputes based on financial return',
    'Reduce unnecessary reserve uncertainty',
    'Improve dispute resolution effectiveness',
    'Increase confidence in financial reporting',
    'Identify missed disputes within the 3-year filing deadline',
  ];

  const quantifiedImpact = [
    {
      label: 'Increase in Initiated Disputes',
      value: '95%',
      icon: 'fas fa-arrow-trend-up',
    },
    {
      label: 'Improvement in Resolving 340B Duplicate Disputes',
      value: '6x',
      icon: 'fas fa-check-circle',
    },
    {
      label: 'Average Recovery from Non-Dispute Processing Error Overpayments',
      value: '$5M+',
      icon: 'fas fa-wallet',
    },
  ];

  const scopeAreas = [
    {
      title: 'Data Integration & Claim Validation',
      icon: 'fas fa-database',
      items: [
        'Direct source-system integration where available',
        'Dispute portfolio consolidation and normalization',
        'Claim-level analysis across available CLD datasets',
        'Execution of dozens of automated validation rules',
      ],
    },
    {
      title: 'Recovery Opportunity & Risk Modeling',
      icon: 'fas fa-chart-line',
      items: [
        'Probability-of-success scoring for each dispute',
        'Manufacturer-favorable opportunity identification',
        'Probability-weighted recovery estimates',
        'Risk-adjusted exposure quantification for unresolved disputes',
      ],
    },
    {
      title: 'Documentation & Executive Reporting',
      icon: 'fas fa-file-signature',
      items: [
        'State-ready support package generation',
        'Executive-ready dashboard and summaries',
        'Actionable prioritization roadmap',
        'Final leadership readout with recommendations',
      ],
    },
  ];

  const faqs = [
    {
      question: 'What is the purpose of the assessment?',
      answer:
        'The assessment helps manufacturers understand which Medicaid disputes represent legitimate recovery opportunities, which disputes may ultimately be payable to the state, and where management should focus resources to maximize financial outcomes.',
    },
    {
      question: 'What types of disputes are reviewed?',
      answer:
        'Kernel reviews all open Medicaid rebate disputes, including disputes involving utilization, eligibility, duplicate claims, unit-of-measure issues, rebate calculations, coverage periods, product eligibility, and other claim-level discrepancies.',
    },
    {
      question:
        'How does Kernel determine whether a dispute is likely to be resolved in favor of the manufacturer?',
      answer:
        'Kernel evaluates each dispute using claim-level validation results, supporting evidence, dispute characteristics, historical resolution trends, and proprietary scoring methodologies. Each dispute receives a probability assessment and risk score.',
    },
    {
      question: 'What are the dozens of validation checks?',
      answer:
        'Kernel applies a comprehensive set of automated validations across available claim-level detail, including duplicate claim detection, utilization validation, NDC verification, unit-of-measure analysis, managed care eligibility checks, coverage verification, calculation validation, and state-specific edits.',
    },
    {
      question: 'What happens when Claim Level Detail (CLD) is available?',
      answer:
        'For periods where CLD remains accessible through vendor portals, Kernel reviews the underlying claims and performs detailed validation testing to identify recovery opportunities and strengthen dispute support.',
    },
    {
      question: 'Does Kernel submit disputes on behalf of the manufacturer?',
      answer:
        'Kernel generates dispute support packages, supporting analyses, and documentation that can be used in dispute resolution discussions. Submission support can be provided as part of a separate managed services engagement if desired.',
    },
    {
      question:
        'Will the assessment identify disputes that should not be pursued?',
      answer:
        'Yes. One of the key objectives is helping management avoid spending resources on disputes with a low likelihood of success. Kernel identifies high-risk disputes and quantifies potential financial exposure.',
    },
    {
      question: 'How are recoverable dollars estimated?',
      answer:
        'Kernel calculates potential recoveries based on claim-level findings, dispute values, validation outcomes, and probability-of-success modeling. The result is both a gross recovery estimate and a probability-adjusted recovery estimate.',
    },
    {
      question: 'Will the assessment help with reserve planning?',
      answer:
        'Yes. The assessment provides visibility into probable recoveries and probable losses, helping Finance and Market Access teams evaluate reserve adequacy and improve financial forecasting.',
    },
    {
      question: 'Who benefits most from this assessment?',
      answer:
        'The assessment is designed for Market Access Leadership, Medicaid Operations Teams, Government Pricing Teams, Revenue Management, Finance Leadership, Compliance Officers, Internal Audit, and Commercial Operations.',
    },
    {
      question: 'How long does the assessment take?',
      answer:
        'Once connectivity is established, most assessments are completed within 2-3 months, depending on dispute volume and data readiness.',
    },
    {
      question: 'What is the final deliverable?',
      answer:
        'The final output is an executive-ready assessment summarizing total open dispute exposure, estimated recoverable value, probability-weighted recovery estimates, state-specific opportunities, high-priority disputes, financial exposure analysis, recommended actions, and an executive recovery roadmap.',
    },
  ];

  const testimonials = [
    {
      quote:
        "OpenPharma has provided a level of visibility into our operations that we simply didn't have before. The platform helps us monitor performance, identify trends, and address challenges proactively, allowing our team to make faster, more informed decisions with greater confidence.",
      author: 'Reimbursement Director, Top 20 Pharmaceutical Company',
    },
    {
      quote:
        "OpenPharma's dispute reporting capabilities have been instrumental in improving our rebate dispute resolution process. The detailed insights and supporting data have enabled us to build stronger working relationships with state agencies, resulting in more productive discussions and a higher rate of disputes being resolved in our favor.",
      author: 'Medicaid Analyst',
    },
    {
      quote:
        'OpenPharma has enhanced our ability to monitor and manage rebate disputes while providing greater confidence in the accuracy of our reserves. The increased transparency and insights have been invaluable to our organization.',
      author: 'VP of Market Access',
    },
  ];

  return (
    <>
      <BlueDotsBackground />

      <div className="text-white px-6 space-y-24">
        {/* Hero */}
        <section className="min-h-screen text-center max-w-5xl mx-auto flex flex-col items-center justify-center">
          <p className="text-sm md:text-base font-semibold tracking-[0.16em] uppercase text-[#464feb] mb-4">
            Medicaid Dispute Recovery & Resolution Assessment
          </p>

          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Know Which Disputes Are Worth Pursuing
          </h1>

          <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-10">
            Independent analysis of open Medicaid rebate disputes, claim-level
            validation opportunities, and probability-weighted recovery
            potential to help leadership prioritize actions and reduce exposure.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 items-center justify-center mt-2 mb-8">
            <Link
              to="/contact"
              className="inline-block bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-xl font-semibold transition"
            >
              Request an Assessment
            </Link>

            <button
              type="button"
              onClick={handleScrollToExecutiveSummary}
              className="inline-block bg-transparent border border-blue-500 text-blue-300 hover:bg-blue-500/10 px-8 py-4 rounded-xl font-semibold transition"
            >
              View Executive Summary
            </button>
          </div>
        </section>

        {/* Executive Summary */}
        <section
          id="executive-summary"
          className="max-w-5xl mx-auto scroll-mt-28"
        >
          <h2 className="text-3xl font-bold text-center mb-8">
            Executive Summary
          </h2>

          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8">
            <p className="mb-4">
              Many manufacturers have substantial value tied up in unresolved
              Medicaid rebate disputes but limited clarity on which items are
              truly recoverable versus likely payable.
            </p>

            <p className="mb-4">
              Kernel&apos;s Medicaid Dispute Recovery & Resolution Assessment
              provides an independent, claim-level evaluation of open disputes
              using direct data integration and automated validation checks to
              identify supportable recovery opportunities.
            </p>

            <p>
              The outcome is a defensible view of gross recovery opportunity,
              probability-adjusted recoverable value, and financial risk
              exposure, enabling leadership to prioritize resources, strengthen
              dispute strategy, and improve reserve confidence.
            </p>
          </div>
        </section>

        {/* Quantified Impact */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Quantified Impact
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            {quantifiedImpact.map((metric, index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-2xl p-8 text-center"
              >
                <i className={`${metric.icon} text-blue-300 text-3xl mb-4`}></i>
                <p className="text-4xl md:text-5xl font-bold text-blue-400 mb-3">
                  {metric.value}
                </p>
                <p className="text-gray-300 font-medium">{metric.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Assessment Highlights */}
        <section className="max-w-6xl mx-auto">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 text-left mb-10">
            <h2 className="text-2xl font-bold mb-6 text-center text-blue-400">
              What You Receive
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-black/20 border border-gray-800 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-blue-300 mb-4">
                  Existing Disputes (Compliance)
                </h3>

                <div className="space-y-3">
                  {whatYouReceive.compliance.map((item, index) => (
                    <div
                      key={index}
                      className="bg-black/30 border border-gray-800 rounded-lg p-4 flex items-start gap-3"
                    >
                      <i className={`${item.icon} text-green-400 mt-1`}></i>
                      <p>{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-black/20 border border-gray-800 rounded-xl p-5">
                <h3 className="text-lg font-semibold text-blue-300 mb-4">
                  CLD Validation ROI (12 Quarter Lookback)
                </h3>

                <div className="space-y-3">
                  {whatYouReceive.roi.map((item, index) => (
                    <div
                      key={index}
                      className="bg-black/30 border border-gray-800 rounded-lg p-4 flex items-start gap-3"
                    >
                      <i className={`${item.icon} text-green-400 mt-1`}></i>
                      <p>{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-8 text-left">
            <h2 className="text-2xl font-bold mb-6 text-center text-blue-400">
              Executive Outcomes
            </h2>

            <ul className="grid md:grid-cols-2 gap-4 text-gray-300">
              {executiveOutcomes.map((item, index) => (
                <li
                  key={index}
                  className="bg-black/30 border border-gray-800 rounded-lg p-4"
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Scope */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Scope</h2>

          <div className="grid md:grid-cols-3 gap-8">
            {scopeAreas.map((area, index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-xl p-6"
              >
                <i className={`${area.icon} text-blue-400 text-3xl mb-4`}></i>

                <h3 className="text-xl font-semibold mb-4">{area.title}</h3>

                <ul className="space-y-2 text-gray-300">
                  {area.items.map((item, i) => (
                    <li key={i}>• {item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Roles & Responsibilities */}
        <section className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Roles & Responsibilities
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4 text-blue-400">
                Customer Responsibilities
              </h3>

              <ul className="space-y-2">
                <li>• Provide required system access</li>
                <li>• Identify key stakeholders</li>
                <li>• Participate in kickoff and review meetings</li>
                <li>• Validate assessment results</li>
              </ul>
            </div>

            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6">
              <h3 className="text-xl font-semibold mb-4 text-blue-400">
                Kernel / OpenPharma Responsibilities
              </h3>

              <ul className="space-y-2">
                <li>• Configure OpenPharma</li>
                <li>• Deliver reporting and recommendations</li>
                <li>• Present final assessment findings</li>
              </ul>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-gray-900 border border-gray-700 rounded-xl p-5"
              >
                <summary className="flex items-center justify-between font-semibold cursor-pointer list-none">
                  <span>{faq.question}</span>
                  <i className="fas fa-chevron-down text-blue-400 ml-4 transition-transform group-open:rotate-180"></i>
                </summary>
                <p className="mt-4 text-gray-300">{faq.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Testimonials */}
        <section className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">
            What Our Customers Say
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-gray-900 border border-gray-700 rounded-2xl p-8 flex flex-col"
              >
                <i className="fas fa-quote-left text-blue-400 text-2xl mb-4"></i>

                <p className="text-gray-300 italic flex-grow">
                  {testimonial.quote}
                </p>

                <p className="mt-6 font-semibold text-blue-400">
                  {testimonial.author}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Closing Section */}
        <section className="text-center max-w-5xl mx-auto">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-10">
            <h2 className="text-3xl font-bold mb-4">
              Know Which Disputes Are Worth Pursuing
            </h2>

            <p className="text-gray-300 mb-8 text-lg">
              Stop guessing which disputes represent real recovery
              opportunities. Kernel provides a data-driven view of your dispute
              portfolio, helping leadership understand recoverable value,
              prioritize resources, reduce risk, and improve financial outcomes.
            </p>

            <Link
              to="/contact"
              className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-xl font-semibold transition inline-block"
            >
              Request a Medicaid Dispute Recovery & Resolution Assessment
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
